import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';
import './CodingInterview.css';
import codeEvaluationApi, { 
  ProblemDetail, 
  TestResult, 
  EvaluationSummary,
  EvaluationStatus,
  ExecuteCodeResponse,
  CodeRun,
  ActivityMetrics,
  PasteEvent,
  KPMSample,
  CodeLengthSample,
  IdlePeriod,
  FocusEvent,
  HintMessage,
} from '../api/codeEvaluationApi';
import ProcessingIndicator from '../features/interview/components/ProcessingIndicator';
import { vibeService } from '../services/VibeService';

// Activity tracking constants
const PASTE_THRESHOLD_CHARS = 20;
const IDLE_THRESHOLD_MS = 30000; // 30 seconds
const SAMPLE_INTERVAL_MS = 10000; // 10 seconds

type LocationState = {
  problemId?: string;
  language?: string;
  mode?: 'timed' | 'practice';
  sessionId?: string;
};

// Timer constants
const TIMED_DURATION_SECONDS = 30 * 60; // 30 minutes

// Maximum runs allowed in timed mode
const MAX_RUNS_TIMED_MODE = 3;

// Session storage key for persisting state across refreshes
const STORAGE_KEY = 'coding_interview_state';

// Interface for persisted state
interface PersistedState {
  code: string;
  timeLeft: number;
  attemptCount: number;
  startTime: number;
  problemId: string | undefined;
  language: string | undefined;
  mode: 'timed' | 'practice' | undefined;
  runHistory: CodeRun[];
  sessionId: string | null;
  hintMessages?: HintMessage[];
  hintDrawerOpen?: boolean;
  // Activity tracking
  activityMetrics?: {
    keystrokeCount: number;
    keystrokeTimestamps: number[];
    pasteEvents: PasteEvent[];
    idlePeriods: IdlePeriod[];
    kpmHistory: KPMSample[];
    codeLengthTimeline: CodeLengthSample[];
    focusEvents: FocusEvent[];
    totalBlurTime: number;
    lastKeystrokeTime: number;
  };
}

const monacoLangMap: Record<string, string> = {
  python: 'python',
  javascript: 'javascript',
  cpp: 'cpp',
  'c++': 'cpp',
  c: 'c',
  java: 'java',
  typescript: 'typescript',
  go: 'go',
  rust: 'rust',
  ruby: 'ruby',
  php: 'php',
  kotlin: 'kotlin',
  'csharp.net': 'csharp',
  'fsharp.net': 'fsharp',
  sqlite3: 'sql',
  rscript: 'plaintext',
  d: 'plaintext',
  fortran: 'plaintext',
  'basic.net': 'plaintext',
  fsi: 'plaintext',
};

// Language versions for display
const languageVersions: Record<string, string> = {
  python: '3.12',
  javascript: '20.11',
  java: '15.0',
  cpp: '10.2',
  'c++': '10.2',
  c: '10.2',
  typescript: '5.0',
  go: '1.16',
  rust: '1.68',
  ruby: '3.0',
  php: '8.2',
  kotlin: '1.8',
  'csharp.net': '5.0',
  'fsharp.net': '5.0',
  sqlite3: '3.36',
  rscript: '4.1',
  d: '10.2',
  fortran: '10.2',
  'basic.net': '5.0',
  fsi: '5.0',
};

// Helper function to get persisted state from sessionStorage
const getPersistedState = (
  currentProblemId: string | undefined, 
  currentLanguage: string | undefined,
  currentMode: 'timed' | 'practice' | undefined
): PersistedState | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const parsed: PersistedState = JSON.parse(stored);
    
    // Only restore if problemId, language, and mode match (same challenge session)
    if (parsed.problemId === currentProblemId && 
        parsed.language === currentLanguage && 
        parsed.mode === currentMode) {
      return parsed;
    }
    
    // Different challenge, clear old state
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  } catch (e) {
    console.error('Failed to parse persisted state:', e);
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

// Status color helper
const getStatusColor = (status: EvaluationStatus): string => {
  switch (status) {
    case 'accepted': return '#22c55e';
    case 'partial': return '#eab308';
    case 'wrong_answer': return '#ef4444';
    case 'time_limit': return '#f97316';
    case 'runtime_error': return '#dc2626';
    case 'compile_error': return '#a855f7';
    default: return '#7a8a9c';
  }
};

// Status label helper
const getStatusLabel = (status: EvaluationStatus): string => {
  switch (status) {
    case 'accepted': return 'Accepted';
    case 'partial': return 'Partial';
    case 'wrong_answer': return 'Wrong Answer';
    case 'time_limit': return 'Time Limit Exceeded';
    case 'runtime_error': return 'Runtime Error';
    case 'compile_error': return 'Compile Error';
    default: return status;
  }
};

const CodingInterview: React.FC = () => {
  const location = useLocation();
  const { problemId, language: navLanguage, mode, sessionId: navSessionId } = (location.state || {}) as LocationState;
  const navigate = useNavigate();
  const isTimedMode = mode === 'timed';

  // Try to restore persisted state
  const persistedState = useMemo(() => getPersistedState(problemId, navLanguage, mode), [problemId, navLanguage, mode]);

  // Problem details from API
  const [problem, setProblem] = useState<ProblemDetail | null>(null);
  const [loadingProblem, setLoadingProblem] = useState<boolean>(true);
  const [problemError, setProblemError] = useState<string>('');
  
  // Code state - restore from persisted or use empty initially (will be set when problem loads)
  const [code, setCode] = useState<string>(persistedState?.code || '');
  const [stdin, setStdin] = useState<string>('');
  
  // Simple run state (Run button with custom input)
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [runOutput, setRunOutput] = useState<string>('');
  const [runError, setRunError] = useState<string>('');
  
  // Evaluation state (Submit button with test cases)
  const [isEvaluating, setIsEvaluating] = useState<boolean>(false);
  const [evaluationState, setEvaluationState] = useState<'idle' | 'evaluating' | EvaluationStatus>('idle');
  const [currentTestResults, setCurrentTestResults] = useState<TestResult[]>([]);
  const [evaluationSummary, setEvaluationSummary] = useState<EvaluationSummary | null>(null);
  const [evaluationError, setEvaluationError] = useState<string>('');
  
  // UI state
  const [leftWidth, setLeftWidth] = useState<number>(380);
  const [activeTab, setActiveTab] = useState<'problem' | 'results'>('problem');
  
  // Timer state for timed mode - restore from persisted state if available
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    if (persistedState?.timeLeft !== undefined && isTimedMode) {
      return persistedState.timeLeft;
    }
    return isTimedMode ? TIMED_DURATION_SECONDS : 0;
  });
  const [isTimerExpired, setIsTimerExpired] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Submission tracking state - restore attempt count and run history
  const [attemptCount, setAttemptCount] = useState<number>(persistedState?.attemptCount || 0);
  const [runHistory, setRunHistory] = useState<CodeRun[]>(persistedState?.runHistory || []);
  const [showSubmitSummary, setShowSubmitSummary] = useState<boolean>(false);
  const [submittedCode, setSubmittedCode] = useState<string>('');
  const [timeTaken, setTimeTaken] = useState<number>(0);
  const [submitError, setSubmitError] = useState<string>('');
  
  // Confirmation dialog states (for timed mode)
  const [showEndConfirm, setShowEndConfirm] = useState<boolean>(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState<boolean>(false);
  const startTimeRef = useRef<number>(persistedState?.startTime || Date.now());
  
  // Check if max runs reached in timed mode
  const maxRunsReached = isTimedMode && attemptCount >= MAX_RUNS_TIMED_MODE;
  
  const isResizingRef = React.useRef<boolean>(false);
  const previousCodeRef = useRef<string>(persistedState?.code || '');
  const isCodeInitRef = useRef<boolean>(false);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasAutoSubmittedRef = useRef<boolean>(false);

  // Session management
  const [sessionId, setSessionId] = useState<string | null>(persistedState?.sessionId || navSessionId || null);
  const sessionStartedRef = useRef<boolean>(!!persistedState?.sessionId);
  const isSubmittingRef = useRef<boolean>(false); // Prevent abandon during submit

  // Hint chat state (practice mode only)
  const [hintMessages, setHintMessages] = useState<HintMessage[]>(persistedState?.hintMessages || []);
  const [hintInput, setHintInput] = useState<string>('');
  const [hintLoading, setHintLoading] = useState<boolean>(false);
  const [hintDrawerOpen, setHintDrawerOpen] = useState<boolean>(persistedState?.hintDrawerOpen || false);
  const [hintError, setHintError] = useState<string>('');
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);
  const hintMessagesEndRef = useRef<HTMLDivElement>(null);
  const hintInputRef = useRef<HTMLTextAreaElement>(null);

  // Activity tracking refs - using refs to avoid re-renders on each keystroke
  const activityRef = useRef({
    keystrokeCount: persistedState?.activityMetrics?.keystrokeCount || 0,
    keystrokeTimestamps: persistedState?.activityMetrics?.keystrokeTimestamps || [] as number[],
    pasteEvents: persistedState?.activityMetrics?.pasteEvents || [] as PasteEvent[],
    idlePeriods: persistedState?.activityMetrics?.idlePeriods || [] as IdlePeriod[],
    kpmHistory: persistedState?.activityMetrics?.kpmHistory || [] as KPMSample[],
    codeLengthTimeline: persistedState?.activityMetrics?.codeLengthTimeline || [] as CodeLengthSample[],
    focusEvents: persistedState?.activityMetrics?.focusEvents || [] as FocusEvent[],
    totalBlurTime: persistedState?.activityMetrics?.totalBlurTime || 0,
    lastKeystrokeTime: persistedState?.activityMetrics?.lastKeystrokeTime || Date.now(),
    blurStartTime: null as number | null,
    idleStartTime: null as number | null,
  });

  // Format time as MM:SS
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Calculate current KPM from recent keystrokes
  const calculateKPM = useCallback((): number => {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    const recent = activityRef.current.keystrokeTimestamps.filter(t => t > oneMinuteAgo);
    return recent.length;
  }, []);

  // Capture all activity metrics for submission
  const captureActivityMetrics = useCallback((): ActivityMetrics => {
    const a = activityRef.current;
    const avgKPM = a.kpmHistory.length > 0
      ? a.kpmHistory.reduce((sum, s) => sum + s.kpm, 0) / a.kpmHistory.length
      : 0;
    const totalCodeTimeSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
    return {
      totalKeystrokes: a.keystrokeCount,
      averageKPM: avgKPM,
      kpmHistory: [...a.kpmHistory],
      pasteEvents: [...a.pasteEvents],
      totalPastedChars: a.pasteEvents.reduce((sum, e) => sum + e.charCount, 0),
      idlePeriods: [...a.idlePeriods],
      totalIdleSeconds: a.idlePeriods.reduce((sum, p) => sum + p.durationSeconds, 0),
      codeLengthTimeline: [...a.codeLengthTimeline],
      focusEvents: [...a.focusEvents],
      totalBlurTimeSeconds: Math.floor(a.totalBlurTime / 1000), // Convert ms to seconds
      totalCodeTimeSeconds, // Total time spent on coding
    };
  }, []);

  const monacoLanguage = useMemo(() => monacoLangMap[navLanguage || 'python'] || 'plaintext', [navLanguage]);

  // Fetch problem details on mount
  useEffect(() => {
    if (!problemId || !navLanguage) {
      setProblemError('No problem selected. Please go back and select a challenge.');
      setLoadingProblem(false);
      return;
    }

    let mounted = true;
    setLoadingProblem(true);
    vibeService.trigger('loading');
    setProblemError('');

    codeEvaluationApi.getProblemDetails(navLanguage, problemId)
      .then((problemData) => {
        if (!mounted) return;
        setProblem(problemData);
        
        // Set starter code from API if not restored from session
        if (!persistedState?.code) {
          setCode(problemData.starter_code);
          previousCodeRef.current = problemData.starter_code;
        }
        isCodeInitRef.current = true;
      })
      .catch((e: any) => {
        if (!mounted) return;
        const message = e?.response?.data?.detail || e?.message || 'Failed to load problem';
        setProblemError(message);
      })
      .finally(() => {
        if (mounted) {
          setLoadingProblem(false);
          vibeService.dismiss();
        }
      });

    return () => { mounted = false; };
  }, [problemId, navLanguage, persistedState?.code]);

  // Start session API call (once per session)
  useEffect(() => {
    if (!problemId || !navLanguage || sessionStartedRef.current || sessionId) return;
    
    sessionStartedRef.current = true;
    console.log('Starting coding session...', { problemId, navLanguage, mode });
    
    codeEvaluationApi.startSession({
      problem_id: problemId,
      language: navLanguage,
      mode: mode || 'practice',
      timed_duration_seconds: isTimedMode ? TIMED_DURATION_SECONDS : undefined,
    }).then(response => {
      if (response.success) {
        console.log('Session started successfully:', response.session_id);
        setSessionId(response.session_id);
      } else {
        console.warn('Start session response not successful:', response);
      }
    }).catch(err => {
      console.error('Failed to start session:', err);
    });
  }, [problemId, navLanguage, mode, isTimedMode]);

  // Persist state to sessionStorage whenever critical values change
  useEffect(() => {
    // Don't persist if already submitted or no problem loaded
    if (showSubmitSummary || isTimerExpired || !problem) return;
    
    const a = activityRef.current;
    const stateToSave: PersistedState = {
      code,
      timeLeft,
      attemptCount,
      startTime: startTimeRef.current,
      problemId,
      language: navLanguage,
      mode,
      runHistory,
      sessionId,
      hintMessages,
      hintDrawerOpen,
      activityMetrics: {
        keystrokeCount: a.keystrokeCount,
        keystrokeTimestamps: a.keystrokeTimestamps.slice(-600), // Keep last 10 min of timestamps
        pasteEvents: a.pasteEvents,
        idlePeriods: a.idlePeriods,
        kpmHistory: a.kpmHistory,
        codeLengthTimeline: a.codeLengthTimeline,
        focusEvents: a.focusEvents,
        totalBlurTime: a.totalBlurTime,
        lastKeystrokeTime: a.lastKeystrokeTime,
      },
    };
    
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (e) {
      console.error('Failed to persist state:', e);
    }
  }, [code, timeLeft, attemptCount, problemId, navLanguage, mode, showSubmitSummary, isTimerExpired, runHistory, problem, sessionId, hintMessages, hintDrawerOpen]);

  // Handle code changes with paste detection (track but allow)
  const handleCodeChange = useCallback((newValue?: string) => {
    const newCode = newValue || '';
    const prevCode = previousCodeRef.current;
    
    // Skip validation during initial code setup
    if (!isCodeInitRef.current) {
      previousCodeRef.current = newCode;
      setCode(newCode);
      return;
    }
    
    // Calculate how many characters were added
    const charsAdded = newCode.length - prevCode.length;
    
    // Detect paste: 20+ chars added at once (track but allow)
    if (charsAdded >= PASTE_THRESHOLD_CHARS) {
      activityRef.current.pasteEvents.push({
        timestamp: Date.now(),
        charCount: charsAdded,
        codeLength: newCode.length,
      });
    }
    
    // Update code
    previousCodeRef.current = newCode;
    setCode(newCode);
  }, []);

  // Timer countdown for timed mode
  useEffect(() => {
    if (!isTimedMode) return;
    
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Timer expired
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          setIsTimerExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTimedMode]);

  // Activity tracking: periodic sampling
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const a = activityRef.current;
      
      // Sample KPM
      a.kpmHistory.push({ timestamp: now, kpm: calculateKPM() });
      
      // Sample code length
      a.codeLengthTimeline.push({ timestamp: now, length: code.length });
      
      // Check for idle period (30+ seconds since last keystroke)
      const idleMs = now - a.lastKeystrokeTime;
      if (idleMs >= IDLE_THRESHOLD_MS && a.idleStartTime === null) {
        a.idleStartTime = a.lastKeystrokeTime;
      }
    }, SAMPLE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [calculateKPM, code.length]);

  // Activity tracking: focus/blur events
  useEffect(() => {
    const handleVisibility = () => {
      const now = Date.now();
      const a = activityRef.current;
      
      if (document.hidden) {
        a.blurStartTime = now;
        a.focusEvents.push({ timestamp: now, type: 'blur' });
      } else {
        if (a.blurStartTime !== null) {
          a.totalBlurTime += now - a.blurStartTime;
          a.blurStartTime = null;
        }
        a.focusEvents.push({ timestamp: now, type: 'focus' });
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Handle tab close / navigation away - show confirmation dialog and call abandon API
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Don't call abandon if we're in the middle of submitting or already submitted
      if (sessionId && !showSubmitSummary && !isSubmittingRef.current) {
        e.preventDefault();
        e.returnValue = '';
        console.log('beforeunload: calling abandon', { sessionId });
        codeEvaluationApi.abandonSession({
          session_id: sessionId,
          last_code: code,
          time_spent_seconds: Math.floor((Date.now() - startTimeRef.current) / 1000),
          reason: 'tab_closed',
        });
      } else {
        console.log('beforeunload: skipping abandon', { sessionId, showSubmitSummary, isSubmitting: isSubmittingRef.current });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sessionId, showSubmitSummary, code]);

  // Auto-submit when timer expires
  useEffect(() => {
    if (isTimerExpired && !hasAutoSubmittedRef.current && !isEvaluating && !showSubmitSummary) {
      hasAutoSubmittedRef.current = true;
      
      // Close any open idle period
      const a = activityRef.current;
      if (a.idleStartTime !== null) {
        const now = Date.now();
        a.idlePeriods.push({
          startTime: a.idleStartTime,
          endTime: now,
          durationSeconds: Math.floor((now - a.idleStartTime) / 1000),
        });
        a.idleStartTime = null;
      }
      
      // Submit to API (async IIFE)
      (async () => {
        if (problemId && navLanguage) {
          const lastRun = runHistory[runHistory.length - 1];
          const effectiveSessionId = sessionId || `fallback_${problemId}_${Date.now()}`;
          
          console.log('Auto-submitting session (timer expired)...', { sessionId: effectiveSessionId });
          
          try {
            await codeEvaluationApi.submitSession({
              session_id: effectiveSessionId,
              problem_id: problemId,
              language: navLanguage,
              mode: mode || 'practice',
              start_time: startTimeRef.current,
              end_time: Date.now(),
              final_code: code,
              attempt_count: attemptCount,
              final_score: lastRun?.summary?.score || 0,
              final_status: lastRun?.summary?.status || 'wrong_answer',
              run_history: runHistory,
              activity_metrics: captureActivityMetrics(),
            });
            console.log('Session auto-submitted successfully');
          } catch (err) {
            console.error('Failed to auto-submit session:', err);
          }
        } else {
          console.error('Cannot auto-submit: missing problemId or language');
        }
      })();
      
      setTimeTaken(TIMED_DURATION_SECONDS);
      setSubmittedCode(code);
      setShowSubmitSummary(true);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [isTimerExpired, isEvaluating, showSubmitSummary, code, sessionId, problemId, navLanguage, mode, attemptCount, runHistory, isTimedMode, captureActivityMetrics]);

  // Run code with custom input (simple execution)
  const runCode = useCallback(async () => {
    if (!navLanguage) {
      setRunError('No language selected');
        return;
      }

    // Check if max runs reached in timed mode
    if (isTimedMode && attemptCount >= MAX_RUNS_TIMED_MODE) {
      setRunError(`Maximum ${MAX_RUNS_TIMED_MODE} runs allowed in timed mode.`);
        return;
      }

    setIsRunning(true);
    setRunOutput('');
    setRunError('');
    
    const currentRunNumber = attemptCount + 1;
    setAttemptCount(currentRunNumber);
    
    try {
      const response: ExecuteCodeResponse = await codeEvaluationApi.executeCode({
        language: navLanguage,
        code,
        stdin: stdin || undefined,
      });
      
      setRunOutput(response.output || '(no output)');
    } catch (e: any) {
      const message = e?.response?.data?.detail || e?.message || 'Execution failed';
      setRunError(message);
    } finally {
      setIsRunning(false);
    }
  }, [navLanguage, code, stdin, isTimedMode, attemptCount]);

  // Submit code for test case evaluation (SSE streaming)
  const submitCode = useCallback(async () => {
    if (!problemId || !navLanguage) {
      setEvaluationError('No problem loaded');
      return;
    }
    
    setIsEvaluating(true);
    setEvaluationState('evaluating');
    setCurrentTestResults([]);
    setEvaluationSummary(null);
    setEvaluationError('');
    setActiveTab('results'); // Switch to results tab
    vibeService.trigger('thinking');
    
    const testResultsCollector: TestResult[] = [];
    
    try {
      await codeEvaluationApi.evaluateCode(
        {
          problem_id: problemId,
          language: navLanguage,
          code,
        },
        {
          onTestResult: (result: TestResult) => {
            testResultsCollector.push(result);
            // Sort by test case number for consistent display
            const sorted = [...testResultsCollector].sort((a, b) => a.test_case - b.test_case);
            setCurrentTestResults(sorted);
          },
          onComplete: (summary: EvaluationSummary) => {
            setEvaluationSummary(summary);
            setEvaluationState(summary.status);
            
            // Trigger vibe based on result
            if (summary.status === 'accepted') {
              vibeService.trigger('celebration');
            } else if (summary.status === 'wrong_answer' || summary.status === 'compile_error' || summary.status === 'runtime_error') {
              vibeService.trigger('error');
            }
            
            // Store this run in history
            const newRun: CodeRun = {
              code: code,
              testResults: [...testResultsCollector].sort((a, b) => a.test_case - b.test_case),
              summary,
              timestamp: Date.now(),
              runNumber: runHistory.length + 1,
            };
            setRunHistory(prev => [...prev, newRun]);
            setIsEvaluating(false);
          },
          onError: (error: string) => {
            setEvaluationError(error);
            setEvaluationState('idle');
            setIsEvaluating(false);
            vibeService.trigger('error');
          },
        }
      );
    } catch (e: any) {
      setEvaluationError(e?.message || 'Evaluation failed');
      setEvaluationState('idle');
      setIsEvaluating(false);
    }
  }, [problemId, navLanguage, code, runHistory.length]);

  // Handle final submission - runs test cases via SSE
  const handleSubmit = useCallback(async () => {
    // Clear any previous error
    setSubmitError('');
    
    // Run test cases evaluation
    await submitCode();
    
    // Calculate time taken
    let elapsed: number;
    if (isTimedMode) {
      elapsed = TIMED_DURATION_SECONDS - timeLeft;
    } else {
      elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
    }
    
    setTimeTaken(elapsed);
    setSubmittedCode(code);
  }, [submitCode, isTimedMode, timeLeft, code]);

  // Format time taken as human readable
  const formatTimeTaken = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) {
      return `${secs} second${secs !== 1 ? 's' : ''}`;
    }
    return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
  };

  // Resizer handlers
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const min = 280;
      const max = Math.max(min, Math.floor(window.innerWidth * 0.5));
      const newWidth = Math.min(max, Math.max(min, e.clientX));
      setLeftWidth(newWidth);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };
    const onMouseUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startResizing = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    isResizingRef.current = true;
  };

  // Auto-scroll hint messages
  useEffect(() => {
    if (hintMessagesEndRef.current) {
      hintMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hintMessages]);

  // Send hint message (practice mode only)
  const sendHintMessage = useCallback(async (retryMessage?: string) => {
    const messageToSend = retryMessage || hintInput.trim();
    if (!messageToSend || hintLoading || !problem) return;

    const userMsg = messageToSend;
    const timestamp = Date.now();

    // Only add user message if it's not a retry (retry message already exists)
    if (!retryMessage) {
      const userMessage: HintMessage = {
        role: 'user',
        content: userMsg,
        timestamp,
        codeSnapshot: code,
        codeLength: code.length,
      };
      setHintMessages(prev => [...prev, userMessage]);
      setHintInput('');
      // Reset textarea height
      if (hintInputRef.current) {
        hintInputRef.current.style.height = 'auto';
      }
    }
    
    setHintLoading(true);
    setHintError('');
    setLastFailedMessage(null);

    try {
      // Build history from previous messages (exclude code snapshots for API)
      const history = hintMessages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      const res = await codeEvaluationApi.sendHintChat({
        problem_id: problemId,
        problem_statement: `${problem.title}\n\n${problem.description}`,
        language: navLanguage || 'python',
        user_code: code,
        user_message: userMsg,
        history,
      }) as any;

      // Check if API returned an error response
      if (res.success === false) {
        console.error('Hint chat error:', res.error || res.message);
        setHintError(res.message || 'Failed to get code hint');
        setLastFailedMessage(userMsg);
        setHintLoading(false);
        return;
      }

      const assistantMessage: HintMessage = {
        role: 'assistant',
        content: res.response,
        timestamp: Date.now(),
      };

      setHintMessages(prev => [...prev, assistantMessage]);
      setLastFailedMessage(null);
    } catch (e: any) {
      console.error('Hint chat error:', e);
      setHintError(e?.message || 'Failed to get code hint');
      setLastFailedMessage(userMsg);
    }

    setHintLoading(false);
  }, [hintInput, hintLoading, problem, code, problemId, navLanguage, hintMessages]);

  // Retry last failed hint message
  const retryHintMessage = useCallback(() => {
    if (lastFailedMessage) {
      sendHintMessage(lastFailedMessage);
    }
  }, [lastFailedMessage, sendHintMessage]);

  // Handle navigation away - call abandon API and clear state
  const handleExit = useCallback(async () => {
    if (sessionId && !showSubmitSummary) {
      await codeEvaluationApi.abandonSession({
        session_id: sessionId,
        last_code: code,
        time_spent_seconds: Math.floor((Date.now() - startTimeRef.current) / 1000),
        reason: 'ended_early',
      });
    }
    sessionStorage.removeItem(STORAGE_KEY);
    navigate('/coding_challenge');
  }, [navigate, sessionId, showSubmitSummary, code]);

  // Confirmation handler for end session (always show warning, same as Mock Interview and Group Discussion)
  const handleEndClick = useCallback(() => {
    setShowEndConfirm(true);
  }, []);

  const handleSubmitClick = useCallback(() => {
    if (!isTimerExpired) {
      setShowSubmitConfirm(true);
    } else {
      handleSubmit();
    }
  }, [isTimerExpired, handleSubmit]);

  // Render problem description with markdown-like formatting
  const renderDescription = () => {
    if (!problem) return null;
    
    return (
      <div className="problem-description">
        <div className="problem-title-header">
          <h2>{problem.title}</h2>
          <div className="problem-badges">
            <span className={`difficulty-badge ${problem.difficulty}`}>{problem.difficulty}</span>
            <span className="category-badge">{problem.category}</span>
          </div>
        </div>
        
        <div className="problem-section">
          <h3>Description</h3>
          <p>{problem.description}</p>
        </div>
        
        <div className="problem-section">
          <h3>Input Format</h3>
          <p>{problem.input_format}</p>
        </div>
        
        <div className="problem-section">
          <h3>Output Format</h3>
          <p>{problem.output_format}</p>
        </div>
        
        <div className="problem-section">
          <h3>Constraints</h3>
          <ul className="constraints-list">
            {problem.constraints.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
        
        <div className="problem-section">
          <h3>Examples</h3>
          {problem.examples.map((ex, i) => (
            <div key={i} className="example-block">
              <div className="example-label">Example {i + 1}</div>
              <div className="example-io">
                <div className="io-block">
                  <span className="io-label">Input:</span>
                  <pre>{ex.input}</pre>
                </div>
                <div className="io-block">
                  <span className="io-label">Output:</span>
                  <pre>{ex.output}</pre>
                </div>
                {ex.explanation && (
                  <div className="io-block explanation">
                    <span className="io-label">Explanation:</span>
                    <p>{ex.explanation}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="problem-section">
          <h3>Test Execution Constraints</h3>
          <div className="constraints-grid">
            <div className="constraint-item">
              <span className="constraint-label">Time Limit</span>
              <span className="constraint-value">{problem.time_limit_seconds}s</span>
            </div>
            <div className="constraint-item">
              <span className="constraint-label">Memory Limit</span>
              <span className="constraint-value">{problem.memory_limit_mb}MB</span>
            </div>
            <div className="constraint-item">
              <span className="constraint-label">Test Cases</span>
              <span className="constraint-value">{problem.test_case_count}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render test case progress UI
  const renderTestProgress = () => {
    if (!problem) return null;
    
    const totalTests = problem.test_case_count;
    
    return (
      <div className="test-progress-container">
        {/* Summary header */}
        {evaluationSummary && (
          <div 
            className="evaluation-summary-bar"
            style={{ backgroundColor: `${getStatusColor(evaluationSummary.status)}20`, borderColor: getStatusColor(evaluationSummary.status) }}
          >
            <span className="summary-status" style={{ color: getStatusColor(evaluationSummary.status) }}>
              {getStatusLabel(evaluationSummary.status)}
            </span>
            <span className="summary-score">
              {evaluationSummary.passed}/{evaluationSummary.total} passed ({evaluationSummary.score.toFixed(1)}%)
            </span>
            <span className="summary-time">{evaluationSummary.total_time_ms}ms</span>
          </div>
        )}
        
        {/* Test case grid */}
        <div className="test-cases-grid">
          {Array.from({ length: totalTests }, (_, i) => {
            const testNum = i + 1;
            const result = currentTestResults.find(r => r.test_case === testNum);
            
            let statusClass = 'pending';
            if (isEvaluating && !result) {
              statusClass = 'running';
            } else if (result) {
              statusClass = result.passed ? 'passed' : 'failed';
            }
            
            return (
              <div key={testNum} className={`test-case-item ${statusClass}`} title={`Test case ${testNum}`}>
                <span className="test-number">{testNum}</span>
                {result && (
                  <span className="test-time">{result.time_ms}ms</span>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Failed test details */}
        {currentTestResults.filter(r => !r.passed).length > 0 && !isEvaluating && (
          <div className="failed-tests-details">
            <h4>Failed Tests</h4>
            {currentTestResults.filter(r => !r.passed).map((result) => (
              <div key={result.test_case} className="failed-test-item">
                <div className="failed-test-header">
                  <span className="failed-test-number">Test #{result.test_case}</span>
                  {result.error && <span className="error-badge">Error</span>}
                </div>
                {result.error ? (
                  <pre className="error-message">{result.error}</pre>
                ) : (
                  <div className="expected-vs-actual">
                    <div className="output-block expected">
                      <span>Expected:</span>
                      <pre>{result.expected}</pre>
                    </div>
                    <div className="output-block actual">
                      <span>Got:</span>
                      <pre>{result.actual}</pre>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Error state */}
        {evaluationError && (
          <div className="evaluation-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{evaluationError}</span>
          </div>
        )}
        
        {/* Idle state hint */}
        {evaluationState === 'idle' && !evaluationError && currentTestResults.length === 0 && (
          <div className="idle-hint">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            <span>Click "Submit" to evaluate your code against all test cases</span>
          </div>
        )}
        
        {/* Finish Interview button - shown after evaluation is complete */}
        {evaluationSummary && !isEvaluating && (
          <div className="finish-interview-container">
            <button 
              className="finish-interview-btn ripple-flash"
              onClick={async () => {
                console.log('Finish Interview clicked');
                
                // Mark as submitting to prevent abandon call
                isSubmittingRef.current = true;
                
                // Stop timer if running
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                }
                
                const elapsed = isTimedMode 
                  ? TIMED_DURATION_SECONDS - timeLeft 
                  : Math.floor((Date.now() - startTimeRef.current) / 1000);
                
                // Submit to API
                if (problemId && navLanguage) {
                  const lastRun = runHistory[runHistory.length - 1];
                  const effectiveSessionId = sessionId || `fallback_${problemId}_${Date.now()}`;
                  
                  console.log('Submitting session to API...', { sessionId: effectiveSessionId, problemId, navLanguage });
                  
                  try {
                    await codeEvaluationApi.submitSession({
                      session_id: effectiveSessionId,
                      problem_id: problemId,
                      language: navLanguage,
                      mode: mode || 'practice',
                      start_time: startTimeRef.current,
                      end_time: Date.now(),
                      final_code: code,
                      attempt_count: attemptCount,
                      final_score: lastRun?.summary?.score || 0,
                      final_status: lastRun?.summary?.status || 'wrong_answer',
                      run_history: runHistory,
                      activity_metrics: captureActivityMetrics(),
                    });
                    console.log('Session submitted successfully to backend');
                  } catch (err) {
                    console.error('Failed to submit session:', err);
                  }
                } else {
                  console.error('Cannot submit: missing problemId or language', { problemId, navLanguage });
                }
                
                sessionStorage.removeItem(STORAGE_KEY);
                setShowSubmitSummary(true);
                setSubmittedCode(code);
                setTimeTaken(elapsed);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              Finish Interview
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="coding-interview-layout">
      <div className="activity-bar" aria-label="Activity Bar"></div>
      
      {/* Top header bar with Luci logo, centered timer, and submit */}
      <div className="coding-header-bar">
        <div className="header-brand" onClick={() => navigate('/candidate')} style={{ cursor: 'pointer' }}>
          <img src="/image_i_do1.png" alt="Luci logo" className="header-logo" />
          <div className="header-brand-text">
            <span className="header-title">Coding Interview</span>
            {isTimedMode && <span className="header-mode-badge">TIMED</span>}
            {mode === 'practice' && <span className="header-mode-badge practice">PRACTICE</span>}
          </div>
        </div>
        
        {/* Timer display - centered */}
        <div className="header-center">
          {isTimedMode && (
            <div className={`timer-display ${timeLeft <= 300 ? 'warning' : ''} ${timeLeft <= 60 ? 'critical' : ''}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="timer-value">{formatTime(timeLeft)}</span>
              {isTimerExpired && <span className="timer-label">Time's up!</span>}
            </div>
          )}
        </div>
        
        {/* Submit button - right side */}
        <button
          className="submit-btn header-submit"
          disabled={isEvaluating || isTimerExpired || showSubmitSummary || !!evaluationSummary}
          onClick={handleSubmitClick}
          title={evaluationSummary ? 'Click Finish Interview to complete' : isTimerExpired ? 'Already submitted' : 'Submit solution'}
        >
          {evaluationSummary ? 'Submitted' : isTimerExpired ? 'Submitted' : 'Submit'}
        </button>
        
        {/* Timer progress bar - thin line under header */}
        {isTimedMode && (
          <div className="timer-progress-bar">
            <div 
              className={`timer-progress-fill ${timeLeft <= 300 ? 'warning' : ''} ${timeLeft <= 60 ? 'critical' : ''}`}
              style={{ width: `${(timeLeft / TIMED_DURATION_SECONDS) * 100}%` }}
            />
          </div>
        )}
      </div>
      
      <div className="left-panel" style={{ width: `${leftWidth}px` }}>
        <div className="panel-header">
          <div className="panel-tabs">
            <button 
              className={`tab ${activeTab === 'problem' ? 'active' : ''}`}
              onClick={() => setActiveTab('problem')}
            >
              PROBLEM
            </button>
            <button 
              className={`tab ${activeTab === 'results' ? 'active' : ''}`}
              onClick={() => setActiveTab('results')}
            >
              RESULTS
              {currentTestResults.length > 0 && (
                <span className="results-badge">
                  {currentTestResults.filter(r => r.passed).length}/{currentTestResults.length}
                </span>
              )}
            </button>
          </div>
        </div>
        
        <div className="left-panel-content">
          {loadingProblem ? (
            <div className="problem-loading">
              <div className="spinner"></div>
              <span>Loading problem...</span>
            </div>
          ) : problemError ? (
            <div className="problem-error">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{problemError}</span>
              <button className="retry-btn" onClick={() => navigate('/coding_challenge')}>
                Back to Challenges
              </button>
        </div>
          ) : activeTab === 'problem' ? (
            renderDescription()
          ) : (
            renderTestProgress()
          )}
          
          {/* End Session button at bottom of left panel */}
          <div className="left-panel-footer">
            <button
              className="end-session-btn"
              onClick={handleEndClick}
              title="End session and go back"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              End session
            </button>
          </div>
        </div>
      </div>
      
      <div
        className="vertical-resizer"
        role="separator"
        aria-orientation="vertical"
        onMouseDown={startResizing}
        title="Drag to resize"
      />
      
      <div className="right-panel">
        <div className="code-panel">
          <div className="panel-toolbar">
            <div className="controls">
              <span className="language-badge">{navLanguage || 'python'}</span>
              {problem && (
                <span className="file-name">{problem.id}.{navLanguage === 'python' ? 'py' : navLanguage === 'javascript' ? 'js' : navLanguage === 'cpp' ? 'cpp' : 'java'}</span>
              )}
            </div>
          </div>
          <div className="editor-container">
            <Editor
              height="100%"
              defaultLanguage={monacoLanguage}
              language={monacoLanguage}
              theme="vs-dark"
              value={code}
              onChange={handleCodeChange}
              onMount={(editor) => {
                // Track keystrokes
                editor.onKeyDown((e) => {
                  // Only count actual typing keys (not modifiers alone)
                  if (!e.ctrlKey && !e.metaKey && !e.altKey) {
                    const now = Date.now();
                    const a = activityRef.current;
                    
                    // Count keystroke
                    a.keystrokeCount++;
                    a.keystrokeTimestamps.push(now);
                    
                    // Prune old timestamps (keep last 60 seconds for KPM calc)
                    const cutoff = now - 60000;
                    a.keystrokeTimestamps = a.keystrokeTimestamps.filter(t => t > cutoff);
                    
                    // Close any open idle period
                    if (a.idleStartTime !== null) {
                      a.idlePeriods.push({
                        startTime: a.idleStartTime,
                        endTime: now,
                        durationSeconds: Math.floor((now - a.idleStartTime) / 1000),
                      });
                      a.idleStartTime = null;
                    }
                    
                    a.lastKeystrokeTime = now;
                  }
                });
              }}
              options={{
                fontSize: 14,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                automaticLayout: true,
              }}
            />
          </div>
        </div>
        
        <div className="execution-panel">
          <div className="panel-toolbar">
            <div className="panel-tabs">
              <button className="tab active">TEST OUTPUT</button>
            </div>
            <div className="controls">
              <label className="label">stdin</label>
              <textarea
                className="stdin-input"
                placeholder="Optional input for testing"
                value={stdin}
                onChange={(e) => setStdin(e.target.value)}
              />
            </div>
            <div className="actions sticky-run">
              {isTimedMode && (
                <span className={`runs-remaining ${maxRunsReached ? 'exhausted' : ''}`}>
                  {MAX_RUNS_TIMED_MODE - attemptCount} run{MAX_RUNS_TIMED_MODE - attemptCount !== 1 ? 's' : ''} left
                </span>
              )}
              <button
                className="run-btn"
                disabled={isRunning || isTimerExpired || maxRunsReached || !problem}
                onClick={runCode}
                title={maxRunsReached ? 'No runs remaining' : isTimerExpired ? 'Time expired' : 'Run code with custom input'}
              >
                {isRunning ? 'Running...' : maxRunsReached ? 'No runs left' : 'Run'}
              </button>
            </div>
          </div>
          <ProcessingIndicator isVisible={isRunning} />
          <div className="output-container">
            {/* Show run output */}
            {runOutput && !isRunning && (
              <div className="run-output-section">
                <div className="output-header">
                  <span>Output</span>
                  {stdin && <span className="stdin-indicator">with custom input</span>}
                </div>
                <pre className="output">{runOutput}</pre>
              </div>
            )}
            
            {/* Error display */}
            {runError && !isRunning && (
              <div className="run-error-section">
                <div className="output-header error">
                  <span>Error</span>
                </div>
                <pre className="output error">{runError}</pre>
              </div>
            )}
            
            {/* Empty state */}
            {!runOutput && !runError && !isRunning && (
              <div className="output-empty">
                <span>Run your code to see output here</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Hint Helper (Practice Mode Only) */}
      {mode === 'practice' && (
        <>
          {!hintDrawerOpen && (
            <button
              className="hint-toggle-btn"
              onClick={() => setHintDrawerOpen(true)}
              title="Get AI Help"
            >
              <img src="/ai_icon.png" alt="AI" className="hint-ai-icon" />
              Need a hint?
            </button>
          )}

          {hintDrawerOpen && (
            <div className="hint-drawer">
              <div className="hint-drawer-header">
                <div className="hint-drawer-title-wrap">
                  <img src="/ai_icon.png" alt="AI" className="hint-ai-icon" />
                  <div className="hint-drawer-title-text">
                    <span className="hint-drawer-title">AI assist</span>
                    <span className="hint-drawer-subtext">Ask for hints meaningfully</span>
                  </div>
                </div>
                <button 
                  className="hint-drawer-close"
                  onClick={() => setHintDrawerOpen(false)}
                  title="Close"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <div className="hint-messages">
                {hintMessages.length === 0 && (
                  <div className="hint-welcome">
                    <p>Ask me for hints about this problem!</p>
                    <p className="hint-welcome-sub">I'll guide you without giving away the solution.</p>
                  </div>
                )}
                {hintMessages.map((msg, idx) => (
                  <div key={idx} className={`hint-msg hint-msg-${msg.role}`}>
                    <div className="hint-msg-content">
                      {msg.role === 'assistant' ? (
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      ) : (
                        msg.content
                      )}
                    </div>
                    <div className="hint-msg-timestamp">
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
                {hintLoading && (
                  <div className="hint-msg hint-msg-assistant hint-loading">
                    <span className="hint-typing">...</span>
                  </div>
                )}
                <div ref={hintMessagesEndRef} />
              </div>

              {hintError && (
                <div className="hint-error">
                  <span>{hintError}</span>
                  <div className="hint-error-actions">
                    {lastFailedMessage && (
                      <button className="hint-error-retry" onClick={retryHintMessage}>
                        Retry
                      </button>
                    )}
                    <button className="hint-error-close" onClick={() => { setHintError(''); setLastFailedMessage(null); }}>×</button>
                  </div>
                </div>
              )}

              <div className="hint-input-area">
                <textarea
                  ref={hintInputRef}
                  className="hint-input"
                  value={hintInput}
                  onChange={e => {
                    setHintInput(e.target.value);
                    // Auto-resize textarea - measure content height precisely
                    const textarea = e.target;
                    textarea.style.height = '0';
                    const scrollHeight = textarea.scrollHeight;
                    textarea.style.height = Math.min(scrollHeight, 110) + 'px';
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendHintMessage();
                    }
                  }}
                  placeholder="Ask for a hint..."
                  disabled={hintLoading}
                  rows={1}
                />
                <button
                  className="hint-send-btn"
                  onClick={() => sendHintMessage()}
                  disabled={hintLoading || !hintInput.trim()}
                  title="Send"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </>
      )}
      
      <div className="vscode-status-bar">
        <div className="status-left">
          Coding Interview
          {isTimedMode && <span className="mode-badge timed">Timed</span>}
          {mode === 'practice' && <span className="mode-badge practice">Practice</span>}
        </div>
        <div className="status-right">
          <span className="status-item">{navLanguage || 'python'} {languageVersions[navLanguage || 'python'] || ''}</span>
          <span className="status-item">{isEvaluating ? '$(sync~spin) Evaluating' : isTimerExpired ? 'Completed' : 'Ready'}</span>
        </div>
      </div>
      
      {/* Toast notification for submit errors */}
      {submitError && (
        <div className="submit-error-toast">
          <span>{submitError}</span>
          <button className="toast-close" onClick={() => setSubmitError('')}>×</button>
        </div>
      )}
      
      {/* Submission Summary Modal */}
      {showSubmitSummary && (
        <div className="submit-summary-overlay">
          <div className="submit-summary-widget">
            <div className="summary-header">
              <div className="summary-icon" style={{ color: runHistory.length > 0 && runHistory[runHistory.length - 1].summary ? getStatusColor(runHistory[runHistory.length - 1].summary!.status) : '#22c55e' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h2>Challenge Submitted!</h2>
            </div>
            
            {/* Final score if available */}
            {runHistory.length > 0 && runHistory[runHistory.length - 1].summary && (
              <div className="final-score" style={{ backgroundColor: `${getStatusColor(runHistory[runHistory.length - 1].summary!.status)}15` }}>
                <span className="score-label">Final Score</span>
                <span className="score-value" style={{ color: getStatusColor(runHistory[runHistory.length - 1].summary!.status) }}>
                  {runHistory[runHistory.length - 1].summary!.score.toFixed(1)}%
                </span>
                <span className="score-status" style={{ color: getStatusColor(runHistory[runHistory.length - 1].summary!.status) }}>
                  {getStatusLabel(runHistory[runHistory.length - 1].summary!.status)}
                </span>
              </div>
            )}
            
            <div className="summary-stats">
              <div className="stat-item">
                <div className="stat-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20V10" />
                    <path d="M18 20V4" />
                    <path d="M6 20v-4" />
                  </svg>
                </div>
                <div className="stat-content">
                  <span className="stat-label">Attempts</span>
                  <span className="stat-value">{attemptCount}</span>
                </div>
              </div>
              
              <div className="stat-item">
                <div className="stat-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="stat-content">
                  <span className="stat-label">Time Taken</span>
                  <span className="stat-value">{formatTimeTaken(timeTaken)}</span>
                </div>
              </div>
            </div>
            
            {/* Show run history */}
            {runHistory.length > 0 && (
              <div className="run-history-section">
                <div className="section-title">Run History</div>
                {runHistory.map((run, index) => (
                  <div key={index} className="run-history-item">
                    <div className="run-header">
                      <span className="run-number">Run #{run.runNumber}</span>
                      {run.summary && (
                        <span className="run-score" style={{ color: getStatusColor(run.summary.status) }}>
                          {run.summary.passed}/{run.summary.total} ({run.summary.score.toFixed(0)}%)
                        </span>
                      )}
                      <span className="run-time">
                        {new Date(run.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <pre className="run-code">{run.code}</pre>
                  </div>
                ))}
              </div>
            )}
            
            <div className="summary-code-section">
              <div className="code-header">
                <span>Final Submitted Code</span>
                <span className="code-language">{navLanguage}</span>
              </div>
              <pre className="summary-code">{submittedCode}</pre>
            </div>
            
            <button 
              className="summary-close-btn"
              onClick={handleExit}
            >
              Back to Challenges
            </button>
          </div>
        </div>
      )}

      {/* End Session Confirmation Modal (Timed Mode) */}
      {showEndConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-widget">
            <h2 className="confirm-title">End session?</h2>
            <p className="confirm-text">
              Are you sure you want to end the challenge? Your progress will not be saved and you'll return to the challenge list.
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn secondary"
                onClick={() => setShowEndConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-btn danger"
                onClick={() => {
                  setShowEndConfirm(false);
                  handleExit();
                }}
              >
                Yes, end session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-widget">
            <h2 className="confirm-title">Submit solution?</h2>
            <p className="confirm-text">
              {isTimedMode 
                ? `Are you sure you want to submit? You have ${formatTime(timeLeft)} remaining. Once submitted, you cannot make any more changes.`
                : 'Are you sure you want to submit your solution? This will evaluate your code against all test cases.'
              }
            </p>
            {isTimedMode && (
              <div className="confirm-stats">
                <div className="confirm-stat">
                  <span className="confirm-stat-label">Runs used</span>
                  <span className="confirm-stat-value">{attemptCount} / {MAX_RUNS_TIMED_MODE}</span>
                </div>
                <div className="confirm-stat">
                  <span className="confirm-stat-label">Time remaining</span>
                  <span className="confirm-stat-value">{formatTime(timeLeft)}</span>
                </div>
              </div>
            )}
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn secondary"
                onClick={() => setShowSubmitConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-btn primary"
                onClick={() => {
                  setShowSubmitConfirm(false);
                  handleSubmit();
                }}
              >
                Yes, submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodingInterview;
